/**
 * MiloSettingsPanel — Phase 6A kill-switch surface.
 *
 * Single toggle: assistant_settings.milo_actions_enabled.
 * When OFF, Milo answers Q&A only and emits no proposal cards.
 * When ON, the Phase 6A L0 navigation classifier may emit
 * runbook-grounded proposals into the Console.
 *
 * No other controls. Read-only otherwise.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Sliders, Lock, ShieldCheck, Loader2, AlertCircle } from 'lucide-react';
import { adminAuthHeader } from '../../data/api';

const API = process.env.REACT_APP_BACKEND_URL;
// Evaluated PER-REQUEST so the real admin token (set at login) is read, not a stale module-load value.
const ADMIN_AUTH = () => adminAuthHeader();

const GOLD = '#C6A75A';
const GOLD_DK = '#8E7A2C';
const DARK = '#1B1812';
const MUTED = '#5C5C66';
const BORDER = 'rgba(0,0,0,0.10)';
const BORDER_GOLD = 'rgba(198,167,90,0.32)';
const SURFACE = '#FFFFFF';
const RED_DK = '#B23A48';

const StatusBadge = ({ children, tone = 'gold' }) => {
  const palette = tone === 'gold'
    ? { bg: 'rgba(198,167,90,0.12)', fg: GOLD_DK, br: BORDER_GOLD }
    : tone === 'green'
    ? { bg: 'rgba(30,115,68,0.10)', fg: '#1E7344', br: 'rgba(30,115,68,0.28)' }
    : { bg: 'rgba(0,0,0,0.05)', fg: MUTED, br: 'rgba(0,0,0,0.16)' };
  return (
    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.6px',
      textTransform: 'uppercase', padding: '3px 9px', borderRadius: 99,
      background: palette.bg, color: palette.fg,
      border: `1px solid ${palette.br}`,
      display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      {children}
    </span>
  );
};

const MiloSettingsPanel = () => {
  const [enabled, setEnabled] = useState(null);   // null while loading
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/milo/actions-enabled`, { headers: ADMIN_AUTH() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setEnabled(!!d.enabled);
      setError(null);
    } catch (e) { setError(String(e.message || e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async () => {
    if (enabled === null || saving) return;
    const next = !enabled;
    setSaving(true);
    setError(null);
    try {
      const r = await fetch(`${API}/api/milo/actions-enabled`, {
        method: 'POST',
        headers: { ...ADMIN_AUTH(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: next }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setEnabled(!!d.enabled);
    } catch (e) { setError(String(e.message || e)); }
    setSaving(false);
  };

  const isOn = enabled === true;

  return (
    <div data-testid="milo-subtab-panel-settings"
      style={{ border: `1px solid ${BORDER}`, borderRadius: 14,
        background: SURFACE, padding: '20px 22px',
        display: 'flex', flexDirection: 'column', gap: 16 }}>

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ width: 42, height: 42, borderRadius: 10,
          background: 'rgba(198,167,90,0.10)',
          border: `1px solid ${BORDER_GOLD}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0 }}>
          <Sliders size={20} color={GOLD_DK} strokeWidth={1.6} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8,
            flexWrap: 'wrap', marginBottom: 4 }}>
            <h3 style={{ margin: 0,
              fontFamily: 'Cormorant Garamond, Georgia, serif',
              fontSize: 22, fontWeight: 700, color: DARK,
              letterSpacing: '0.2px' }}>
              Milo Settings
            </h3>
            <StatusBadge tone="gold">Phase 6A</StatusBadge>
            <StatusBadge tone="muted">Admin-only</StatusBadge>
          </div>
          <p style={{ margin: 0, fontSize: 12, color: MUTED, lineHeight: 1.55,
            maxWidth: 720 }}>
            Operator-only controls for Milo's behaviour. The kill switch
            below is the master gate for all Phase 6A safe-action
            proposals.
          </p>
        </div>
      </div>

      {/* Kill switch card */}
      <div data-testid="milo-actions-killswitch-card"
        style={{ border: `1px solid ${BORDER}`, borderRadius: 12,
          background: '#FFFFFF', padding: '18px 20px',
          display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', gap: 18 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8,
            flexWrap: 'wrap', marginBottom: 6 }}>
            <ShieldCheck size={15} color={GOLD_DK} />
            <span style={{ fontFamily: 'Cormorant Garamond, Georgia, serif',
              fontSize: 18, fontWeight: 700, color: DARK }}>
              Milo safe-action proposals
            </span>
            {enabled !== null && (
              <span data-testid="milo-killswitch-state-pill"
                style={{ fontSize: 10, fontWeight: 800,
                  letterSpacing: '0.06em', textTransform: 'uppercase',
                  padding: '3px 9px', borderRadius: 99,
                  background: isOn ? 'rgba(30,115,68,0.10)' : 'rgba(0,0,0,0.05)',
                  color: isOn ? '#1E7344' : MUTED,
                  border: `1px solid ${isOn ? 'rgba(30,115,68,0.28)' : 'rgba(0,0,0,0.16)'}` }}>
                {isOn ? 'On' : 'Off'}
              </span>
            )}
          </div>
          <p style={{ margin: 0, fontSize: 12, color: MUTED, lineHeight: 1.6 }}>
            When <strong style={{ color: DARK }}>off</strong> (default),
            Milo answers admin questions in the Console with citations
            only. When
            <strong style={{ color: DARK }}> on</strong>, Milo may
            additionally suggest runbook-grounded
            <strong style={{ color: DARK }}> navigation actions </strong>
            (open the right tab, open the cited runbook). Proposals never
            mutate data — the operator must always perform the action in
            the destination tab themselves.
          </p>
        </div>
        <button data-testid="milo-killswitch-toggle"
          onClick={toggle}
          disabled={enabled === null || saving}
          aria-checked={isOn}
          role="switch"
          style={{
            width: 64, height: 34, borderRadius: 999,
            border: `1px solid ${isOn ? 'rgba(30,115,68,0.32)' : 'rgba(0,0,0,0.16)'}`,
            background: isOn
              ? 'linear-gradient(135deg, #1E7344, #145B33)'
              : 'rgba(0,0,0,0.06)',
            position: 'relative', cursor: saving ? 'wait' : 'pointer',
            transition: 'all 0.18s ease', flexShrink: 0,
            padding: 0,
          }}>
          <span style={{
            position: 'absolute', top: 3,
            left: isOn ? 32 : 3,
            width: 26, height: 26, borderRadius: '50%',
            background: '#FFFFFF',
            boxShadow: '0 1px 3px rgba(0,0,0,0.20)',
            transition: 'left 0.18s ease',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {saving && <Loader2 size={12} color={GOLD_DK}
              style={{ animation: 'milo-spin 0.9s linear infinite' }} />}
          </span>
        </button>
      </div>

      {error && (
        <div data-testid="milo-killswitch-error"
          style={{ padding: '8px 12px', borderRadius: 8,
            background: 'rgba(178,58,72,0.06)',
            border: '1px solid rgba(178,58,72,0.22)',
            fontSize: 12, color: RED_DK,
            display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <AlertCircle size={13} /> {error}
        </div>
      )}

      <div style={{ border: `1px solid ${BORDER}`, borderRadius: 12,
        background: '#FFFFFF', padding: '14px 18px',
        display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.10em',
          textTransform: 'uppercase', color: MUTED }}>
          What Phase 6A allows
        </span>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12,
          color: DARK, lineHeight: 1.7 }}>
          <li><strong>L0 navigation only.</strong> Open a tab, open a runbook.</li>
          <li><strong>No L1 prefill</strong> in this phase. Compose Invite is untouched.</li>
          <li><strong>No L2 writes.</strong> No data mutation can be proposed or executed.</li>
          <li><strong>Runbook-grounded only.</strong> If no runbook supports it, no proposal is emitted.</li>
        </ul>
      </div>

      <div data-testid="milo-settings-boundary-note"
        style={{ display: 'flex', alignItems: 'flex-start', gap: 10,
          padding: '11px 14px', borderRadius: 10,
          background: 'rgba(198,167,90,0.05)',
          border: `1px solid ${BORDER_GOLD}` }}>
        <Lock size={12} color={GOLD_DK}
          style={{ marginTop: 1, flexShrink: 0 }} />
        <div style={{ fontSize: 11, color: MUTED, lineHeight: 1.55 }}>
          Toggling this switch is an
          <strong style={{ color: DARK }}> operator-only </strong>
          action. Milo cannot toggle it. Every proposal Milo emits while
          this is on is logged to the Action Log subtab as a row, and a
          dismiss is logged as a separate additive row — the original
          proposal is never mutated.
        </div>
      </div>

      <style>{`
        @keyframes milo-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default MiloSettingsPanel;
